import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useFeatures } from 'flagged'
import { styled } from '../../../../components/mui/Styles/index'
import { Backdrop, Box, CircularProgress, Modal } from '../../../../components/mui/Core/index'
import SelectRole from './components/SelectRole/select-role'
import SelectOrganization from './components/SelectOrganization/select-organization'
import SendInvite from './components/SendInvite/send-invite'
import InviteUserNavigation from './components/InviteUserNavigation/invite-user-navigation'
import { resetInviteUserSlice } from './invite-user-modal-slice'
import { signupUser } from '../../../../api/user/signup-user'
import Constants from '../../../../constants/en.json'
import { fetchAllUsers } from '../../user-management-slice'
import { setSnackbar } from '../../../../components/GenericSnackbar/generic-snackbar-slice'

const CenteredContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  minHeight: '329px',
  width: '496px',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: theme.palette.common.white,
  border: 'none !important',
  borderRadius: '12px',
  boxShadow: 24,
  paddingLeft: '24px',
  paddingRight: '24px',
  paddingTop: '20px',
  paddingBottom: '20px',
}))

function InviteUserModal({ isOpen, handleCloseModal }) {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const features = useFeatures()
  const isUserServiceEnabled = features.isUserServiceEnabled
  const authenticatedUser = useSelector((state) => state.login.authenticatedUser)
  const currentStep = useSelector((state) => state.inviteUser.currentStep)
  const email = useSelector((state) => state.inviteUser.email)
  const selectedRole = useSelector((state) => state.inviteUser.selectedRole)
  const selectedOrganization = useSelector((state) => state.inviteUser.selectedOrganization)
  const clientWarehouseCode = useSelector((state) => state.inviteUser.clientWarehouseCode)

  const handleCloseAction = (_event, reason) => {
    if (reason === 'backdropClick') {
      return
    }
    handleCloseModal()
    dispatch(resetInviteUserSlice())
  }

  const handleOnCreateUserSuccess = () => {
    setIsLoading(false)
    handleCloseAction()
    dispatch(fetchAllUsers({ isUserServiceEnabled }))
    const message = Constants.userManagement.INVITE_SENT_SUCCESSFULLY.replace('__USERNAME__', email)
    dispatch(setSnackbar({ message, severity: 'success', action: true }))
  }

  const handleOnCreateUserError = () => {
    setIsLoading(false)
    const message = Constants.userManagement.errors.UNABLE_TO_INVITE_USER.replace(
      '__USERNAME__',
      email,
    )
    dispatch(setSnackbar({ message, severity: 'error', action: true }))
  }

  const handleSubmitInvite = () => {
    // TODO: remove dummy OR condition on below fields when api makes them optional.
    setIsLoading(true)
    const request = {
      userName: email,
      email,
      clientName: selectedOrganization?.clientName || 'dummy',
      clientCode: selectedOrganization?.clientCode || 'dummy',
      warehouseCode: selectedOrganization?.warehouseCode || clientWarehouseCode,
      dmsFlag: selectedOrganization?.dmsFlag,
      customerNumber: selectedOrganization?.customerNumber || 'dummy',
      customerName: selectedOrganization?.customerName || 'dummy',
      invitedBy: authenticatedUser?.email,
      roles: [selectedRole?.roleName],
      appName: selectedRole?.appName,
    }
    signupUser(request)
      .then(() => handleOnCreateUserSuccess())
      .catch(() => handleOnCreateUserError())
  }

  function getCurrentStep() {
    switch (currentStep) {
      case 1:
        return <SelectRole />
      case 2:
        return <SelectOrganization />
      case 3:
        return <SendInvite />
      default:
        return null
    }
  }

  const loadingStyle = { borderRadius: '12px' }

  return (
    <Modal disableEscapeKeyDown open={isOpen} onClose={handleCloseAction}>
      <CenteredContainer>
        <Backdrop open={isLoading} sx={loadingStyle}>
          <CircularProgress color='inherit' />
        </Backdrop>
        {getCurrentStep()}
        <Box>
          <InviteUserNavigation
            handleCloseAction={handleCloseAction}
            handleSubmitAction={handleSubmitInvite}
          />
        </Box>
      </CenteredContainer>
    </Modal>
  )
}

InviteUserModal.propTypes = {
  isOpen: PropTypes.bool,
  handleCloseModal: PropTypes.func,
}

export default InviteUserModal
